import client, { toInventoryList } from './inventory-request';
import { mapMedicineSearchRows } from '../utils/medicineSearch';
import { findExactInventoryItem } from '../utils/newMedicineEntity';

// Categories — all calls via EMR proxy (/app/api/inventory/...), not direct /api/v1 (CORS).
// API: GET /inventory/categories/
export const getCategories  = (p) => client.get('/inventory/categories/', p);
// API: GET /inventory/categories/{id}/
export const getCategory    = (id) => client.get(`/inventory/categories/${id}/`);
// API: POST /inventory/categories/
export const createCategory = (body) => client.post('/inventory/categories/', body);
// API: PATCH /inventory/categories/{id}/
export const updateCategory = (id, body) => client.patch(`/inventory/categories/${id}/`, body);
// API: DELETE /inventory/categories/{id}/
export const deleteCategory = (id) => client.delete(`/inventory/categories/${id}/`);

// UOMs
// API: GET /inventory/uoms/
export const getUoms   = (p) => client.get('/inventory/uoms/', p);
// API: GET /inventory/uoms/{id}/
export const getUom    = (id) => client.get(`/inventory/uoms/${id}/`);
// API: POST /inventory/uoms/
export const createUom = (body) => client.post('/inventory/uoms/', body);
// API: PATCH /inventory/uoms/{id}/
export const updateUom = (id, body) => client.patch(`/inventory/uoms/${id}/`, body);
// API: DELETE /inventory/uoms/{id}/
export const deleteUom = (id) => client.delete(`/inventory/uoms/${id}/`);

// Items
// API: GET /inventory/items/
export const getItems                 = (p) => client.get('/inventory/items/', p);
// API: GET /inventory/items/{id}/
export const getItem                  = (id) => client.get(`/inventory/items/${id}/`);
// API: POST /inventory/items/
export const createItem               = (body) => client.post('/inventory/items/', body);
// API: PATCH /inventory/items/{id}/
export const updateItem               = (id, body) => client.patch(`/inventory/items/${id}/`, body);
// API: DELETE /inventory/items/{id}/
export const deleteItem               = (id) => client.delete(`/inventory/items/${id}/`);
// API: GET /inventory/items/by-code/{code}/
export const getItemByCode            = (code) => client.get(`/inventory/items/by-code/${code}/`);
// API: GET /inventory/items/{id}/rules/
export const getItemValidationProfile = (id) => client.get(`/inventory/items/${id}/rules/`);

const ITEM_SEARCH_PAGE_SIZE = 200; // the items API's maximum limit
const ITEM_SEARCH_MAX_PAGES = 10;

/**
 * The item named exactly `name` (any group, any store, active or not), or null.
 * `search` is a contains-match ordered by item_name, so an exact match can sit
 * past the first page. Pages until one comes back short. Throws rather than
 * answering "not found" when it gives up, because "not found" leads to a create.
 */
export async function findInventoryItemByName(name) {
  const search = String(name || '').trim();
  if (!search) return null;
  for (let page = 0; page < ITEM_SEARCH_MAX_PAGES; page += 1) {
    const rows = toInventoryList(await getItems({
      search,
      limit: ITEM_SEARCH_PAGE_SIZE,
      offset: page * ITEM_SEARCH_PAGE_SIZE,
    }));
    const match = findExactInventoryItem(rows, search);
    if (match) return match;
    if (rows.length < ITEM_SEARCH_PAGE_SIZE) return null;
  }
  throw new Error(`Too many items match "${search}" to rule out a duplicate.`);
}

// EMR formulary typeahead — GET /search_medicines/?name=
export async function searchMedicines(name) {
  const q = (name || '').trim();
  if (q.length < 2) return [];
  const rows = await client.get('/search_medicines/', { name: q });
  return mapMedicineSearchRows(rows);
}

// Same endpoint, rows left unmapped so `source` survives: 'database' is the EMR
// medicine master (public.main_medicine), 'drug_registry' is ABDM.
export const searchMedicineSources = (name) => client.get('/search_medicines/', { name: String(name || '').trim() });

// Stores — see api/stores.js (proxied /app/api/inventory/stores/)
export {
  getStores, getStore, createStore, updateStore, deleteStore,
} from './stores';
